import { Component, computed, effect, inject, input, NgZone, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { DashboardWorkedHoursObject } from '@libs/types/src/services/object-type/dashboard/dashboard-worked-hours.object';
import { UserGridItemObject } from '@it2go/types';
import { TimePeriodEnum } from '@libs/types/src/common/enum/timePeriod.enum';
import { UIChart } from '@openng/optimus-ui/chart';
import { Chart, ChartData, ChartOptions, TooltipModel } from 'chart.js';
import { DashboardChartWidgetStore } from '../dashboard-chart-widget.store';
import {
    dashboardChartTooltipPosition,
    dashboardPeriodDateKeys,
    dashboardServiceIds,
    dashboardShiftMinutes,
} from '../dashboard-chart-widget.utils';
import { dashboardWorkedHoursFragment, dashboardWorkerResourceFragment } from '../dashboard-chart-widget.query';
import { LoadingComponent } from '../../../../../../components/kit/loading/loading.component';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';
import { SessionService } from '../../../../../../services/session.service';
import { CalendarWorkingHoursResolver } from '../../../../../shared/service/calendar/calendar-working-hours.resolver';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { Operator } from '../../../../../../../../../data-grid/src/grid/grid-request.dto';
import { formatDuration } from '../../../../../shared/pipes/format-duration.pipe';
import { TranslateService } from '@ngx-translate/core';
import { DashboardChartTooltipComponent } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.component';
import { DashboardChartTooltipData } from '../dashboard-chart-tooltip/dashboard-chart-tooltip.types';
import {
    DateScrollerInputComponent,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.component';
import {
    DateScrollerRange,
} from '../../../../../../components/kit/input/date-scroller-input-v2/date-scroller-input.types';

@Component({
    selector: 'app-worked-hours-widget',
    templateUrl: './worked-hours-widget.component.html',
    styleUrl: './worked-hours-widget.component.scss',
    providers: [DashboardChartWidgetStore],
    imports: [
        FormField,
        DateScrollerInputComponent,
        DashboardChartTooltipComponent,
        UIChart,
        LoadingComponent,
        EmptyStateComponent,
    ],
})
export class WorkedHoursWidgetComponent {
    protected readonly store = inject(DashboardChartWidgetStore);
    private readonly session = inject(SessionService);
    private readonly workingHoursResolver = inject(CalendarWorkingHoursResolver);
    private readonly translate = inject(TranslateService);
    private readonly zone = inject(NgZone);
    private readonly chartColor = '#4b82db';

    public readonly items = input<{ serviceId: string; widget: UserWidgetSetting }[]>([]);

    protected readonly dateFormData = signal({ date: new Date() as Date | null });
    protected readonly dateForm = form(this.dateFormData);
    protected readonly period = signal<DateScrollerRange>(this.currentMonth());
    protected readonly statisticsLoading = this.store.loadingFor('getDashboardWorkedHours');
    protected readonly resourcesLoading = this.store.loadingFor('getUsers');
    protected readonly loading = computed(() => this.statisticsLoading() || this.resourcesLoading());
    protected readonly rows = computed<DashboardWorkedHoursObject[]>(
        () => this.store.data.getDashboardWorkedHours?.() ?? [],
    );
    protected readonly resources = computed<UserGridItemObject[]>(
        () => this.store.data.getUsers?.()?.items ?? [],
    );
    protected readonly dateKeys = computed(() => dashboardPeriodDateKeys(this.period()));
    protected readonly chartData = computed<ChartData<'line'>>(() => ({
        labels: this.dateKeys().map(date => this.dateLabel(date)),
        datasets: [{
            label: '',
            data: this.dateKeys().map(date => this.rows()
                .filter(row => row.date === date)
                .reduce((total, row) => total + row.workedMinutes / 60, 0)),
            borderColor: this.chartColor,
            backgroundColor: this.chartColor,
            tension: 0.2,
        }],
    }));
    protected readonly chartTooltip = signal<DashboardChartTooltipData | null>(null);
    protected readonly chartOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: false,
                external: ({ chart, tooltip }) => this.updateChartTooltip(chart, tooltip),
            },
        },
        scales: {
            y: { beginAtZero: true },
        },
    };

    constructor() {
        effect(() => {
            const serviceIds = dashboardServiceIds(this.items());
            const period = this.period();
            if (!serviceIds.length) return;

            this.store.api.getDashboardWorkedHours({
                serviceIds,
                dateFrom: period.dateFrom,
                dateTo: period.dateTo,
            }, dashboardWorkedHoursFragment);
        });

        effect(() => {
            const userIds = [...new Set(this.rows().map(row => row.userId))];
            if (!userIds.length) return;
            const filter = new FilterBuilder()
                .where('id', userIds, Operator.IN)
                .limit(1_000);

            this.store.api.getUsers(filter.filter(), dashboardWorkerResourceFragment);
        });
    }

    private updateChartTooltip(chart: Chart, tooltip: TooltipModel<'line'>): void {
        if (!tooltip.opacity) {
            this.hideChartTooltip();
            return;
        }

        const dataIndex = tooltip.dataPoints[0]?.dataIndex;
        if (dataIndex === undefined) {
            this.hideChartTooltip();
            return;
        }
        const date = this.dateKeys()[dataIndex];
        const rows = this.rows().filter(row => row.date === date);
        const workedMinutes = rows.reduce((total, row) => total + row.workedMinutes, 0);
        if (workedMinutes <= 0) {
            this.hideChartTooltip();
            return;
        }

        this.zone.run(() => this.chartTooltip.set({
            ...dashboardChartTooltipPosition(chart.width, chart.height, tooltip.caretX, tooltip.caretY),
            title: String(chart.data.labels?.[dataIndex] ?? ''),
            summary: [{
                label: this.translate.instant('dashboard.workedHours.total'),
                value: formatDuration(Math.round(workedMinutes)),
                color: this.chartColor,
            }],
            table: rows.length ? {
                columns: [
                    { label: this.translate.instant('dashboard.workedHours.worker') },
                    { label: this.translate.instant('dashboard.workedHours.worked'), align: 'right' },
                    { label: this.translate.instant('dashboard.workedHours.shift'), align: 'right' },
                ],
                rows: rows.map(row => {
                    const scheduledMinutes = this.getScheduledMinutes(row.userId, date);
                    return [
                        row.worker,
                        formatDuration(Math.round(row.workedMinutes)),
                        scheduledMinutes > 0
                            ? `${Math.round(row.workedMinutes / scheduledMinutes * 100)} %`
                            : '–',
                    ];
                }),
            } : undefined,
        }));
    }

    private hideChartTooltip(): void {
        this.zone.run(() => this.chartTooltip.set(null));
    }

    private getScheduledMinutes(userId: string, date: string): number {
        const resource = this.resources().find(item => item.id === userId);
        if (!resource) return 0;

        const workingHours = this.workingHoursResolver.resolve({
            date: new Date(`${date}T12:00:00`),
            resources: [resource],
            services: this.store.globalGlobalStore.organizationStore.servicesInContext(),
            selectedServiceIds: dashboardServiceIds(this.items()),
        })[0];

        return dashboardShiftMinutes(workingHours?.intervals ?? []);
    }

    private dateLabel(value: string): string {
        return new Intl.DateTimeFormat(this.session.locale, { day: 'numeric', month: 'numeric' })
            .format(new Date(`${value}T12:00:00`));
    }

    private currentMonth(): DateScrollerRange {
        const dateFrom = new Date();
        dateFrom.setHours(0, 0, 0, 0);
        dateFrom.setDate(1);
        const dateTo = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0, 23, 59, 59, 999);
        return { dateFrom, dateTo, type: TimePeriodEnum.Month, length: 1 };
    }

    protected readonly TimePeriodEnum = TimePeriodEnum;
}
